import axios from 'axios';
import {
  EquipmentDashboardSummary,
  EquipmentRepairDetail,
  EquipmentRepairRow,
  FleetStatusImpact,
  Vehicle,
  VehicleDefect,
  VehicleDefectExport,
  VehicleFilters,
  VehicleFilterOptions,
  VehicleInsurance,
  VehicleListResponse,
  VehicleOdometerReading,
  VehiclePmInspection
} from '../types/Vehicle';
import { sharedRequest } from '../utils/sharedRequest';

const BASE_URL = process.env.REACT_APP_API_URL || '/api';

/**
 * A refused request. `fields` holds the server's messages for named fields, so a dialog can show
 * each one under its field (FORM-01) instead of in a banner.
 */
export class VehicleApiError extends Error {
  status?: number;
  fields: Record<string, string>;
  constructor(message: string, status?: number, fields: Record<string, string> = {}) {
    super(message);
    this.status = status;
    this.fields = fields;
  }
}

/** Field errors from any error: VehicleApiError fields, else the message under `fallbackField`. */
export function fieldErrorsOf(error: unknown, fallbackField?: string): Record<string, string> {
  if (error instanceof VehicleApiError && Object.keys(error.fields).length > 0) return error.fields;
  const message = error instanceof Error ? error.message : 'Something went wrong. Try again.';
  return fallbackField ? { [fallbackField]: message } : { _form: message };
}

/** V3-061: one rate in a unit's history. Dates are YYYY-MM-DD; effectiveTo is null for the current rate. */
export interface RateHistoryRow {
  id: number;
  rate: number;
  effectiveFrom: string;
  effectiveTo: string | null;
  reason: string;
  changedBy: string | null;
  changedAt: string;
}

export interface ResolveDefectsInput {
  defectIds: number[];
  outcome: string;
  remedy?: string;
  fixedById?: number;
  fixedAt?: string;
  signOff?: boolean;
  duplicateOfId?: number;
}

class VehicleService {
  private handleError(error: any): Error {
    if (error.response) {
      // Server responded with error status
      const data = error.response.data || {};
      const message = data.error || data.message || 'An error occurred';
      return new VehicleApiError(message, error.response.status, data.fields || {});
    } else if (error.request) {
      // Request made but no response received
      return new Error('No response from server. Please check your connection.');
    } else {
      // Something else happened
      return new Error(error.message || 'An unexpected error occurred');
    }
  }

  async getVehiclesPaginated(
    page: number = 1,
    pageSize: number = 10,
    filters: VehicleFilters = {}
  ): Promise<VehicleListResponse> {
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        pageSize: pageSize.toString(),
        ...Object.fromEntries(
          Object.entries(filters).filter(([_, value]) => value !== undefined && value !== '')
        )
      });

      const response = await axios.get(`${BASE_URL}/vehicles?${params}`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async getVehicleById(id: number): Promise<Vehicle> {
    try {
      // V3-036: the unit page and its tabs share one request for the unit.
      return await sharedRequest(`vehicles/${id}`, async () => (await axios.get(`${BASE_URL}/vehicles/${id}`)).data);
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async createVehicle(data: Partial<Vehicle>): Promise<Vehicle> {
    try {
      const response = await axios.post(`${BASE_URL}/vehicles`, data);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async updateVehicle(id: number, data: Partial<Vehicle>): Promise<Vehicle> {
    try {
      const response = await axios.put(`${BASE_URL}/vehicles/${id}`, data);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async getVehicleFilterOptions(): Promise<VehicleFilterOptions> {
    try {
      const response = await axios.get(`${BASE_URL}/vehicles/filter-options`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async getVehicleProfilePreview(vehicleId: number): Promise<any> {
    try {
      const response = await axios.get(`${BASE_URL}/vehicles/${vehicleId}/profile-preview`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async getShopBoard(): Promise<Vehicle[]> {
    try {
      const response = await axios.get(`${BASE_URL}/vehicles/shop-board`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async getDashboardSummary(): Promise<EquipmentDashboardSummary> {
    try {
      const response = await axios.get(`${BASE_URL}/vehicles/dashboard-summary`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async setAvailability(
    id: number,
    data: { availability: string; reason?: string; estimatedReturnToServiceDate?: string | null }
  ): Promise<Vehicle> {
    try {
      const response = await axios.post(`${BASE_URL}/vehicles/${id}/availability`, data);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async bulkAvailability(
    ids: number[],
    data: { availability: string; reason?: string; estimatedReturnToServiceDate?: string | null }
  ): Promise<Vehicle[]> {
    try {
      const response = await axios.post(`${BASE_URL}/vehicles/bulk/availability`, { ids, ...data });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async setFleetStatus(id: number, data: { status: string; reason: string }): Promise<Vehicle> {
    try {
      const response = await axios.post(`${BASE_URL}/vehicles/${id}/fleet-status`, data);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async getFleetStatusImpact(id: number): Promise<FleetStatusImpact> {
    try {
      const response = await axios.get(`${BASE_URL}/vehicles/${id}/fleet-status-impact`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async transferForemanArea(id: number, data: { foremanAreaId: number; note?: string }): Promise<Vehicle> {
    try {
      const response = await axios.post(`${BASE_URL}/vehicles/${id}/transfer-foreman-area`, data);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async bulkTransferForemanArea(ids: number[], data: { foremanAreaId: number; note?: string }): Promise<Vehicle[]> {
    try {
      const response = await axios.post(`${BASE_URL}/vehicles/bulk/transfer-foreman-area`, { ids, ...data });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async transferServiceArea(id: number, data: { serviceAreaId: number; foremanAreaId: number; note?: string }): Promise<Vehicle> {
    try {
      const response = await axios.post(`${BASE_URL}/vehicles/${id}/transfer-service-area`, data);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /** V3-061: a rate change with its Effective Date (YYYY-MM-DD) and Reason. */
  async setRate(id: number, data: { rate: number; effectiveDate: string; reason: string }): Promise<Vehicle> {
    try {
      const response = await axios.post(`${BASE_URL}/vehicles/${id}/rate`, data);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /** V3-061: the unit's rate history, newest first. */
  async getRateHistory(id: number): Promise<RateHistoryRow[]> {
    try {
      const response = await axios.get(`${BASE_URL}/vehicles/${id}/rate-history`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async setRoadClass(id: number, data: { roadClass: 'ON_ROAD' | 'OFF_ROAD' }): Promise<Vehicle> {
    try {
      const response = await axios.post(`${BASE_URL}/vehicles/${id}/road-class`, data);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async setGpsModem(id: number, data: { gpsModemId?: string | null }): Promise<Vehicle> {
    try {
      const response = await axios.post(`${BASE_URL}/vehicles/${id}/gps-modem`, data);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async recordOdometer(
    id: number,
    data: { value: number | string; override?: boolean; reason?: string }
  ): Promise<VehicleOdometerReading> {
    try {
      const response = await axios.post(`${BASE_URL}/vehicles/${id}/odometer`, data);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async uploadPhoto(id: number, file: File): Promise<Vehicle> {
    try {
      const form = new FormData();
      form.append('file', file);
      const response = await axios.post(`${BASE_URL}/vehicles/${id}/photo`, form, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async clearPhoto(id: number): Promise<Vehicle> {
    try {
      const response = await axios.delete(`${BASE_URL}/vehicles/${id}/photo`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async addInsurance(id: number, data: {
    policyNumber: string;
    insurer: string;
    effectiveFrom: string;
    expires: string;
    documentUrl?: string;
  }): Promise<VehicleInsurance> {
    try {
      const response = await axios.post(`${BASE_URL}/vehicles/${id}/insurance`, data);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /** Served through the API because the B2 bucket is private; the session cookie authorizes the request. */
  insuranceDocumentUrl(id: number, insuranceId: number): string {
    return `${BASE_URL}/vehicles/${id}/insurance/${insuranceId}/document`;
  }

  async deleteInsurance(id: number, insuranceId: number): Promise<void> {
    try {
      await axios.delete(`${BASE_URL}/vehicles/${id}/insurance/${insuranceId}`);
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async raiseDefect(id: number, data: {
    description: string;
    category?: string;
    source?: string;
    severity?: string;
    reporterName?: string;
  }): Promise<VehicleDefect> {
    try {
      const response = await axios.post(`${BASE_URL}/vehicles/${id}/defects`, data);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /** Resolve one defect or several at once (EQ-07, EQ-12). */
  async resolveDefects(id: number, data: ResolveDefectsInput): Promise<VehicleDefect[]> {
    try {
      const response = await axios.post(`${BASE_URL}/vehicles/${id}/defects/resolve`, data);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /** Sign Off one defect or several resolved without sign-off (V3-053); one path for row and bulk. */
  async signOffDefects(id: number, defectIds: number[]): Promise<VehicleDefect[]> {
    try {
      const response = await axios.post(`${BASE_URL}/vehicles/${id}/defects/sign-off`, { defectIds });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async getDefectCategories(): Promise<string[]> {
    try {
      const response = await axios.get(`${BASE_URL}/vehicles/defect-categories`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async setNextCvip(id: number, nextCVIP: string | null): Promise<Vehicle> {
    try {
      const response = await axios.post(`${BASE_URL}/vehicles/${id}/next-cvip`, { nextCVIP });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async exportDefects(id: number, range: { from: string; to: string }): Promise<VehicleDefectExport> {
    try {
      const response = await axios.get(`${BASE_URL}/vehicles/${id}/defects/export`, { params: range });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async getPmInspections(
    id: number,
    options: { from?: string; to?: string; includeItems?: boolean } = {}
  ): Promise<VehiclePmInspection[]> {
    try {
      const { includeItems, ...range } = options;
      const response = await axios.get(`${BASE_URL}/vehicles/${id}/pm-inspections`, {
        params: { ...range, ...(includeItems ? { includeItems: '1' } : {}) }
      });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async getPmInspection(id: number, pmId: number): Promise<VehiclePmInspection> {
    try {
      const response = await axios.get(`${BASE_URL}/vehicles/${id}/pm-inspections/${pmId}`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async getRepairDetail(id: number, lineId: number): Promise<EquipmentRepairDetail> {
    try {
      const response = await axios.get(`${BASE_URL}/vehicles/${id}/repair-history/${lineId}`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /** Newest 200 lines, or every line between `from` and `to` (YYYY-MM-DD, inclusive). */
  async getRepairHistory(id: number, q?: string, range?: { from: string; to: string }): Promise<EquipmentRepairRow[]> {
    try {
      const params = { ...(q ? { q } : {}), ...(range ?? {}) };
      const response = await axios.get(`${BASE_URL}/vehicles/${id}/repair-history`, {
        params: Object.keys(params).length > 0 ? params : undefined
      });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }
}

export default new VehicleService();

